import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Modal } from '@mui/material';
import { ArrowRight, CornerDownLeft, Search } from 'lucide-react';

import { isSuperadminRole } from 'core/router/routes';
import { navGroups, type NavItem } from './navItems';

type CommandPaletteProps = {
  open: boolean;
  onClose: () => void;
};

type FlatItem = NavItem & { groupLabel: string };

/**
 * Command-палитра по разделам приложения. Открывается из шапки или ⌘K.
 * Фильтрация по подстроке имени и группы (регистронезависимо).
 * Навигация — Up/Down/Enter, закрытие — Esc.
 */
const CommandPalette = ({ open, onClose }: CommandPaletteProps) => {
  const navigate = useNavigate();
  const userRole = useSelector((s: any) => s.reducer.role);
  const isAdmin = useSelector((s: any) => s.reducer.isAdmin);

  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  const isAllowed = (item: NavItem): boolean => {
    if (!userRole) return false;
    if (isSuperadminRole(userRole, isAdmin)) return true;
    if (item.adminOnly) return Boolean(isAdmin);
    if (item.allRoles) return true;
    if (!item.permission) return true;
    const allowed: string[] = (userRole.permissions ?? []).map((p: { name: string }) => p.name);
    return allowed.includes(item.permission);
  };

  const allItems: FlatItem[] = useMemo(() => {
    const out: FlatItem[] = [];
    for (const group of navGroups) {
      for (const item of group.items) {
        if (isAllowed(item)) out.push({ ...item, groupLabel: group.label });
      }
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRole, isAdmin]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return allItems;
    return allItems.filter(
      (it) =>
        it.label.toLowerCase().includes(q) ||
        it.groupLabel.toLowerCase().includes(q) ||
        (it.path && it.path.toLowerCase().includes(q))
    );
  }, [query, allItems]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIndex(0);
      // фокусируем input после открытия
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [open]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  useEffect(() => {
    if (!listRef.current) return;
    const el = listRef.current.querySelector<HTMLElement>(`[data-idx="${activeIndex}"]`);
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const handleSelect = (item: FlatItem) => {
    onClose();
    navigate(item.path);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(filtered.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const target = filtered[activeIndex];
      if (target) handleSelect(target);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: 'rgba(15, 16, 20, 0.45)',
            backdropFilter: 'blur(2px)',
          },
        },
      }}
      sx={{ alignItems: 'flex-start', justifyContent: 'center', display: 'flex', pt: '14vh' }}
    >
      <div
        onKeyDown={handleKeyDown}
        style={{
          width: 'min(640px, 90vw)',
          background: 'var(--shell-bg-surface)',
          border: '1px solid var(--shell-border-subtle)',
          borderRadius: 'var(--shell-radius-lg)',
          boxShadow: 'var(--shell-shadow-overlay)',
          outline: 'none',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '70vh',
        }}
      >
        {/* search */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '10px 14px',
            borderBottom: '1px solid var(--shell-border-subtle)',
          }}
        >
          <Search size={16} strokeWidth={1.75} color="var(--shell-text-tertiary)" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Найти раздел…"
            style={{
              flex: 1,
              border: 'none',
              outline: 'none',
              background: 'transparent',
              fontSize: 14,
              fontFamily: 'inherit',
              color: 'var(--shell-text-primary)',
            }}
          />
          <kbd
            style={{
              fontSize: 10,
              padding: '1px 5px',
              border: '1px solid var(--shell-border-default)',
              borderRadius: 4,
              background: 'var(--shell-bg-subtle)',
              color: 'var(--shell-text-tertiary)',
              fontFamily: 'inherit',
            }}
          >
            Esc
          </kbd>
        </div>

        {/* results */}
        <div ref={listRef} style={{ overflowY: 'auto', flex: 1, padding: 4 }}>
          {filtered.length === 0 ? (
            <div
              style={{
                padding: '32px 16px',
                textAlign: 'center',
                color: 'var(--shell-text-tertiary)',
                fontSize: 13,
              }}
            >
              Ничего не найдено
            </div>
          ) : (
            (() => {
              const rows: React.ReactNode[] = [];
              let prevGroup = '';
              filtered.forEach((item, idx) => {
                if (item.groupLabel !== prevGroup) {
                  rows.push(
                    <div
                      key={`group-${item.groupLabel}`}
                      style={{
                        padding: '8px 10px 4px',
                        fontSize: 10,
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.6,
                        color: 'var(--shell-text-tertiary)',
                      }}
                    >
                      {item.groupLabel}
                    </div>
                  );
                  prevGroup = item.groupLabel;
                }
                const Icon = item.icon;
                const active = idx === activeIndex;
                rows.push(
                  <button
                    key={item.id}
                    type="button"
                    data-idx={idx}
                    onClick={() => handleSelect(item)}
                    onMouseEnter={() => setActiveIndex(idx)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      width: '100%',
                      padding: '8px 10px',
                      borderRadius: 6,
                      border: 'none',
                      background: active ? 'var(--shell-bg-hover)' : 'transparent',
                      color: 'var(--shell-text-primary)',
                      cursor: 'pointer',
                      textAlign: 'left',
                      fontSize: 13,
                    }}
                  >
                    <Icon
                      size={14}
                      strokeWidth={1.75}
                      color={active ? 'var(--shell-accent-primary)' : 'var(--shell-text-tertiary)'}
                    />
                    <span style={{ flex: 1 }}>{item.label}</span>
                    <span
                      style={{
                        fontSize: 11,
                        color: 'var(--shell-text-tertiary)',
                        fontFamily: 'var(--shell-font-mono, monospace)',
                      }}
                    >
                      {item.path}
                    </span>
                    {active && (
                      <ArrowRight size={12} strokeWidth={2} color="var(--shell-accent-primary)" />
                    )}
                  </button>
                );
              });
              return rows;
            })()
          )}
        </div>

        {/* footer */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '8px 14px',
            borderTop: '1px solid var(--shell-border-subtle)',
            background: 'var(--shell-bg-subtle)',
            fontSize: 11,
            color: 'var(--shell-text-tertiary)',
          }}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <kbd style={kbdStyle}>↑</kbd>
            <kbd style={kbdStyle}>↓</kbd>
            навигация
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <CornerDownLeft size={11} strokeWidth={1.75} />
            открыть
          </span>
          <div style={{ flex: 1 }} />
          <span>{filtered.length}</span>
        </div>
      </div>
    </Modal>
  );
};

const kbdStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 18,
  height: 16,
  padding: '0 4px',
  fontSize: 10,
  fontFamily: 'inherit',
  border: '1px solid var(--shell-border-default)',
  borderRadius: 4,
  background: 'var(--shell-bg-surface)',
  color: 'var(--shell-text-secondary)',
};

export default CommandPalette;
